import React, { useEffect, useState, createContext } from "react";
import { useJsApiLoader } from "@react-google-maps/api";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import WelcomeScreen from "./components/WelcomeScreen";
import PropertyWizard from "./components/PropertyWizard";
import logo from "./assets/logo-cotify-horizontal-negro.png";
import Home from "./pages/Home/Home";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { loadUserCreditContext } from './api/propertiesApi';
import SessionGuard from "./components/SessionGuard";
import AppToaster from "./components/AppToaster";


const apiUrl = process.env.REACT_APP_API_URL;
const panelUrl = process.env.REACT_APP_PANEL_URL;

// Nuevo contexto para créditos / plan / quota.
//
// available: solo se usa cuando plan === 'legacy' (modelo viejo de tokens).
// Para los planes modernos (free/pro/business), `available` es ignorado y
// manda `weeklyQuota.remaining`.
//
// canCreate(): helper único que el resto de la app usa para decidir si
// dejar crear una ACM o mostrar upsell — abstrae la diferencia legacy vs
// plan moderno.
export const CreditContext = createContext({
  available: 0,           // tokens_assigned (solo legacy)
  name: "",
  plan: "legacy",
  weeklyQuota: null,      // solo free: {limit, used, remaining, resets_at, unlimited}
  monthlyTokens: null,    // solo pro/business: {limit, remaining, used, refill_at, exhausted}
  setCreditData: () => { },
});

const libraries = ["places", "drawing", "geometry"];

// Componente para proteger rutas
const AuthGuard = ({ children }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      setIsLoading(false);
      return;
    }

    const timeout = setTimeout(() => {
      if (!localStorage.getItem('auth_token')) {
        window.location.href = `${panelUrl}/auth/login`;
      }
      setIsLoading(false);
    }, 2000); // Espera 2 segundos por el postMessage

    return () => clearTimeout(timeout);
  }, [navigate]);

  if (isLoading) return <div>Cargando...</div>;
  return children;
};


// Wrapper para el PropertyWizard con navegación al completar
const PropertyWizardWrapper = () => {
  const navigate = useNavigate();

  const handleComplete = (propertyData) => {
    if (!propertyData) {
      console.error("propertyData es undefined!");
    } else {
      console.log("Propiedad recibida:", {
        type_operation: propertyData.type_operation,
        type_property: propertyData.type_property,
        bedrooms: propertyData.bedrooms,

      });
    }

    navigate("/home", { state: { propertyData } });
  };

  return (
    <div className="property-form-page">
      <img src={logo} alt="Cotify" className="property-form-page__logo" />
      <PropertyWizard onComplete={handleComplete} />
    </div>
  );
};

const App = () => {
  // available = null mientras no se cargó el perfil. Así PropertyWizard
  // puede distinguir "aún no sé" de "tenés 0 créditos" y evitar redirect
  // prematuro antes de que getUserProfile termine.
  const [creditData, setCreditData] = useState({
    available: null,
    name: "",
    plan: "legacy",
    weeklyQuota: null,
    monthlyTokens: null,
  });

  // Capturar token desde query param y guardarlo
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get("token");

    if (tokenParam) {
      localStorage.setItem("auth_token", tokenParam);
      const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;
      const isSecure = window.location.protocol === "https:";
      const parts = [
        `auth_token=${tokenParam}`,
        "path=/",
        "max-age=3600",
        "SameSite=Lax",
      ];
      if (cookieDomain) parts.push(`domain=${cookieDomain}`);
      if (isSecure) parts.push("Secure");
      document.cookie = parts.join("; ");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Obtener datos del usuario. Reintenta si falla (token recién puesto en
  // localStorage puede no estar propagado al siguiente request, o la red
  // puede fallar transitoriamente). Evitamos dejar `available=null` para
  // siempre si hay un glitch.
  useEffect(() => {
    let cancelled = false;
    const fetchUserData = async (attempt = 0) => {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      try {
        const ctx = await loadUserCreditContext();
        if (cancelled) return;
        setCreditData({
          available: ctx.available,
          name: ctx.name,
          plan: ctx.plan,
          weeklyQuota: ctx.weeklyQuota,
          monthlyTokens: ctx.monthlyTokens,
        });
        if (ctx.credit_token) localStorage.setItem('credit_token', ctx.credit_token);
      } catch (error) {
        console.error(`Error al obtener datos de usuario (intento ${attempt + 1}):`, error);
        if (!cancelled && attempt < 2) {
          // Backoff corto: 400ms, 1200ms
          setTimeout(() => fetchUserData(attempt + 1), 400 * (attempt + 1) * 2);
        }
      }
    };
    fetchUserData();
    return () => { cancelled = true; };
  }, []);

  // Cargar Google Maps
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  if (!isLoaded) return <div>Cargando...</div>;

  return (
    <CreditContext.Provider value={{ ...creditData, setCreditData }}>
      <AppToaster />
      <Router>
        <Routes>
          <Route path="/" element={<AuthGuard><WelcomeScreen /></AuthGuard>} />
          <Route path="/property-form" element={<AuthGuard><PropertyWizardWrapper /></AuthGuard>} />
         
          <Route path="/home" element={<AuthGuard> <SessionGuard><Home /></SessionGuard></AuthGuard>} />
          
        </Routes>
      </Router>
    </CreditContext.Provider>
  );
};

export default App;
